/**
 * T117: Prompt Generator
 * Combined component for template variable input and rendered output
 */

import { useState } from 'react'
import { usePromptVariables } from '@/hooks/usePromptVariables'
import { useGeneratePrompt } from '@/hooks/useGeneratePrompt'
import { VariableInputForm } from './variable-input-form'
import { RenderedPromptView } from './rendered-prompt-view'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorMessage } from '@/components/common/error-message'
import type { GeneratePromptResponse } from '@/services/prompts'

interface PromptGeneratorProps {
  promptId: string
}

export function PromptGenerator({ promptId }: PromptGeneratorProps) {
  const [result, setResult] = useState<GeneratePromptResponse | null>(null)

  const {
    data: variablesData,
    isLoading: isLoadingVariables,
    error: variablesError,
    refetch,
  } = usePromptVariables(promptId)

  const generateMutation = useGeneratePrompt()

  const handleGenerate = async (values: Record<string, string>) => {
    const response = await generateMutation.mutateAsync({
      id: promptId,
      input: { variables: values },
    })
    setResult(response)
  }

  if (isLoadingVariables) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (variablesError) {
    return (
      <ErrorMessage
        title="Failed to load variables"
        message={variablesError.message}
        onRetry={refetch}
      />
    )
  }

  const variables = variablesData?.variables ?? []

  return (
    <div className="space-y-6">
      <VariableInputForm
        variables={variables}
        onSubmit={handleGenerate}
        isLoading={generateMutation.isPending}
      />
      <RenderedPromptView result={result} />
      {generateMutation.error && (
        <ErrorMessage
          title="Generation failed"
          message={generateMutation.error.message}
        />
      )}
    </div>
  )
}
